<template>
  <section class="fe-checkout-page">
    <div class="container fe-checkout-page__inner">
      <div v-if="cartStore.cart_products.length === 0" class="fe-checkout-empty">
        <div class="fe-checkout-empty__icon" aria-hidden="true">
          <i class="fas fa-shopping-basket"></i>
        </div>
        <h2 class="fe-checkout-empty__title">No items to checkout</h2>
        <p class="fe-checkout-empty__text">Your cart is empty. Add products before placing an order.</p>
        <router-link to="/shop" class="fe-co-btn fe-co-btn--primary">Return to shop</router-link>
      </div>

      <form v-else class="fe-checkout-form" @submit.prevent="onSubmit">
        <div class="row g-4 g-xl-5 align-items-start">
          <!-- Billing: wider column -->
          <div class="col-lg-8 col-xl-8">
            <div class="fe-co-card fe-co-card--billing">
              <div class="fe-co-card__head">
                <h2 class="fe-co-card__title">Billing details</h2>
                <p class="fe-co-card__sub">Shipping and contact information for this order.</p>
              </div>

              <div class="fe-co-section">
                <h3 class="fe-co-label-section">Saved addresses</h3>
                <p v-if="addresses.length === 0" class="fe-co-muted">
                  No saved addresses yet. Use the button to add an address — it will be saved to your account.
                </p>
                <button
                  v-if="addresses.length === 0"
                  type="button"
                  class="fe-co-btn fe-co-btn--secondary fe-co-btn--sm"
                  @click="openAddressModal"
                >
                  <i class="fas fa-plus"></i>
                  Add address
                </button>
                <div v-else class="fe-address-list">
                  <label
                    v-for="address in addresses"
                    :key="address.id"
                    class="fe-address-option"
                    :class="{ 'fe-address-option--active': selectedAddressId === address.id && !useAlternateAddress }"
                  >
                    <input
                      type="radio"
                      :id="'address-' + address.id"
                      name="billingAddress"
                      :value="address.id"
                      v-model="selectedAddressId"
                      @change="onSelectSavedAddress(address)"
                    />
                    <span class="fe-address-option__body">
                      <span class="fe-address-option__name">{{ address.name }}</span>
                      <span class="fe-address-option__line">
                        {{ address.address }}, {{ address.city }}, {{ address.zip_code }}
                      </span>
                    </span>
                  </label>
                </div>
                <div v-if="addresses.length > 0" class="fe-co-address-actions">
                  <button
                    v-if="!useAlternateAddress"
                    type="button"
                    class="fe-co-linkish"
                    @click="startAlternateAddress"
                  >
                    Use a different address for this order only
                  </button>
                  <button
                    v-else
                    type="button"
                    class="fe-co-linkish"
                    @click="backToSavedAddresses"
                  >
                    Choose a saved address instead
                  </button>
                </div>
                <p v-if="useAlternateAddress" class="fe-co-tip">
                  Enter a one-time shipping address below. You can optionally save it to your account before placing the order.
                </p>
              </div>

              <h3 class="fe-co-label-section fe-co-label-section--form">
                {{ useAlternateAddress || addresses.length === 0 ? 'Delivery details' : 'Or enter different details' }}
              </h3>

              <div
                v-if="showSaveAddressToggle"
                class="fe-co-save-profile mb-3"
              >
                <label class="fe-co-check">
                  <input v-model="saveNewAddressToProfile" type="checkbox" />
                  <span>Save this address to my account</span>
                </label>
              </div>

              <div class="row g-3 g-lg-4 fe-co-form-grid">
                <div class="col-12">
                  <label class="fe-field-label">Country <span class="fe-req">*</span></label>
                  <select
                    v-model="formData.country"
                    class="fe-input fe-select"
                    :disabled="isShippingAddressLocked"
                    :class="{ 'fe-input--locked': isShippingAddressLocked }"
                  >
                    <option value="">Select a country</option>
                    <option v-for="country in countries" :key="country.id" :value="country.id">
                      {{ country.name }}
                    </option>
                  </select>
                  <p v-if="isShippingAddressLocked" class="fe-co-field-hint">
                    Country and street details come from your selected saved address. Use "Use a different address for
                    this order only" to edit them.
                  </p>
                  <div v-if="errors.country" class="fe-error">{{ errors.country }}</div>
                </div>

                <div class="col-12">
                  <label class="fe-field-label">Full name <span class="fe-req">*</span></label>
                  <input v-model="formData.name" type="text" class="fe-input" placeholder="Your name" />
                  <div v-if="errors.name" class="fe-error">{{ errors.name }}</div>
                </div>

                <div class="col-12">
                  <label class="fe-field-label">Company name <span class="fe-optional">(optional)</span></label>
                  <input
                    v-model="formData.company_name"
                    type="text"
                    class="fe-input"
                    placeholder="Company"
                  />
                  <div v-if="errors.company_name" class="fe-error">{{ errors.company_name }}</div>
                </div>

                <div class="col-12">
                  <label class="fe-field-label">Street address <span class="fe-req">*</span></label>
                  <input
                    v-model="formData.address"
                    type="text"
                    class="fe-input"
                    :class="{ 'fe-input--locked': isShippingAddressLocked }"
                    placeholder="Street address"
                    :disabled="isShippingAddressLocked"
                  />
                  <div v-if="errors.address" class="fe-error">{{ errors.address }}</div>
                </div>

                <div class="col-12">
                  <label class="fe-field-label fe-sr-only">Address line 2</label>
                  <input
                    v-model="formData.address_2"
                    type="text"
                    class="fe-input"
                    :class="{ 'fe-input--locked': isShippingAddressLocked }"
                    placeholder="Apartment, suite, unit (optional)"
                    :disabled="isShippingAddressLocked"
                  />
                </div>

                <div class="col-md-6">
                  <label class="fe-field-label">City <span class="fe-req">*</span></label>
                  <input
                    v-model="formData.city"
                    type="text"
                    class="fe-input"
                    :class="{ 'fe-input--locked': isShippingAddressLocked }"
                    placeholder="City"
                    :disabled="isShippingAddressLocked"
                  />
                  <div v-if="errors.city" class="fe-error">{{ errors.city }}</div>
                </div>

                <div class="col-md-6">
                  <label class="fe-field-label">Postcode / Zip <span class="fe-req">*</span></label>
                  <input
                    v-model="formData.zip_code"
                    type="text"
                    class="fe-input"
                    :class="{ 'fe-input--locked': isShippingAddressLocked }"
                    placeholder="Postcode"
                    :disabled="isShippingAddressLocked"
                  />
                  <div v-if="errors.zip_code" class="fe-error">{{ errors.zip_code }}</div>
                </div>

                <div class="col-md-6">
                  <label class="fe-field-label">Email <span class="fe-req">*</span></label>
                  <input v-model="formData.email" type="email" class="fe-input" placeholder="you@email.com" />
                  <div v-if="errors.email" class="fe-error">{{ errors.email }}</div>
                </div>

                <div class="col-md-6">
                  <label class="fe-field-label">Phone <span class="fe-req">*</span></label>
                  <input v-model="formData.phone" type="text" class="fe-input" placeholder="Phone number" />
                  <div v-if="errors.phone" class="fe-error">{{ errors.phone }}</div>
                </div>

                <div class="col-12">
                  <label class="fe-field-label">Order notes <span class="fe-optional">(optional)</span></label>
                  <textarea
                    v-model="formData.order_note"
                    class="fe-input fe-textarea"
                    rows="4"
                    placeholder="Delivery instructions or other notes"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Order summary -->
          <div class="col-lg-4 col-xl-4">
            <div class="fe-co-card fe-co-order">
              <h3 class="fe-co-order__title">Your order</h3>
              <div class="fe-co-order__list">
                <div
                  v-for="item in cartStore.cart_products"
                  :key="item.id"
                  class="fe-co-line"
                >
                  <div class="fe-co-line__info">
                    <span class="fe-co-line__name">{{ item.title }}</span>
                    <span class="fe-co-line__qty">× {{ item.orderQuantity }}</span>
                  </div>
                  <span class="fe-co-line__price">
                    LKR {{ lineTotal(item).toFixed(2) }}
                  </span>
                </div>
              </div>
              <div class="fe-co-totals">
                <div class="fe-co-totals__row">
                  <span>Subtotal</span>
                  <span class="fe-co-money">LKR {{ cartStore.totalPriceQuantity.total.toFixed(2) }}</span>
                </div>
                <div class="fe-co-totals__row">
                  <span>Shipping</span>
                  <span class="fe-co-money">LKR {{ Number(shipCost).toFixed(2) }}</span>
                </div>
                <div class="fe-co-totals__row fe-co-totals__row--grand">
                  <span>Total</span>
                  <span class="fe-co-money fe-co-money--grand">
                    LKR {{ (cartStore.totalPriceQuantity.total + shipCost).toFixed(2) }}
                  </span>
                </div>
              </div>
              <button type="submit" class="fe-co-btn fe-co-btn--primary fe-co-btn--block">
                <i class="fas fa-lock"></i>
                Place order
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>

    <Teleport to="body">
      <div
        v-if="addressModalOpen"
        class="fe-co-modal-overlay"
        role="presentation"
        @click.self="closeAddressModal"
      >
        <div
          class="fe-co-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fe-co-modal-title"
        >
          <div class="fe-co-modal__head">
            <h3 id="fe-co-modal-title" class="fe-co-modal__title">Add delivery address</h3>
            <button type="button" class="fe-co-modal__close" aria-label="Close" @click="closeAddressModal">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <p class="fe-co-modal__intro">
            This address will be saved to your account for future orders.
          </p>
          <form class="fe-co-modal__form" @submit.prevent="submitAddressModal">
            <div class="fe-co-modal__grid">
              <div class="fe-co-modal__field">
                <label class="fe-field-label">Full name <span class="fe-req">*</span></label>
                <input v-model="modalForm.name" type="text" class="fe-input" />
                <div v-if="modalErrors.name" class="fe-error">{{ modalErrors.name }}</div>
              </div>
              <div class="fe-co-modal__field">
                <label class="fe-field-label">Phone</label>
                <input v-model="modalForm.phone" type="text" class="fe-input" />
              </div>
              <div class="fe-co-modal__field fe-co-modal__field--full">
                <label class="fe-field-label">Country <span class="fe-req">*</span></label>
                <select v-model="modalForm.country_id" class="fe-input fe-select">
                  <option value="">Select a country</option>
                  <option v-for="country in countries" :key="country.id" :value="country.id">
                    {{ country.name }}
                  </option>
                </select>
                <div v-if="modalErrors.country_id" class="fe-error">{{ modalErrors.country_id }}</div>
              </div>
              <div class="fe-co-modal__field fe-co-modal__field--full">
                <label class="fe-field-label">Street address <span class="fe-req">*</span></label>
                <input v-model="modalForm.address" type="text" class="fe-input" />
                <div v-if="modalErrors.address" class="fe-error">{{ modalErrors.address }}</div>
              </div>
              <div class="fe-co-modal__field fe-co-modal__field--full">
                <label class="fe-field-label fe-optional">Apartment (optional)</label>
                <input v-model="modalForm.address_2" type="text" class="fe-input" />
              </div>
              <div class="fe-co-modal__field">
                <label class="fe-field-label">City <span class="fe-req">*</span></label>
                <input v-model="modalForm.city" type="text" class="fe-input" />
                <div v-if="modalErrors.city" class="fe-error">{{ modalErrors.city }}</div>
              </div>
              <div class="fe-co-modal__field">
                <label class="fe-field-label">Postcode <span class="fe-req">*</span></label>
                <input v-model="modalForm.zip_code" type="text" class="fe-input" />
                <div v-if="modalErrors.zip_code" class="fe-error">{{ modalErrors.zip_code }}</div>
              </div>
            </div>
            <p v-if="modalErrors.general" class="fe-error">{{ modalErrors.general }}</p>
            <div class="fe-co-modal__actions">
              <button type="button" class="fe-co-btn fe-co-btn--ghost" @click="closeAddressModal">Cancel</button>
              <button type="submit" class="fe-co-btn fe-co-btn--primary" :disabled="modalSaving">
                <span v-if="modalSaving">Saving…</span>
                <span v-else>Save &amp; continue</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
  </section>
</template>

<script>
import { useCartStore } from '@/stores/useCartStore'
import { toast } from 'vue3-toastify'
import { getAuthToken } from '@/services/axios'

export default {
  name: 'CheckoutArea',
  data() {
    return {
      cartStore: useCartStore(),
      shipCost: 0,
      user: JSON.parse(localStorage.getItem('user')) || { name: 'Guest', email: '' },
      formData: {
        name: '',
        country: '',
        address: '',
        address_2: '',
        city: '',
        email: '',
        phone: '',
        zip_code: '',
        company_name: '',
        order_note: '',
      },
      selectedAddressId: null,
      useAlternateAddress: false,
      saveNewAddressToProfile: true,
      addressModalOpen: false,
      modalSaving: false,
      modalForm: {
        name: '',
        phone: '',
        country_id: '',
        address: '',
        address_2: '',
        city: '',
        zip_code: '',
      },
      modalErrors: {},
      countries: [],
      addresses: [],
      errors: {},
    }
  },
  computed: {
    showSaveAddressToggle() {
      return this.addresses.length === 0 || this.useAlternateAddress
    },
    /** Shipping lines only — name, email, phone, notes stay editable. */
    isShippingAddressLocked() {
      if (!this.addresses.length) return false
      if (this.useAlternateAddress) return false
      return (
        this.selectedAddressId != null &&
        this.selectedAddressId !== ''
      )
    },
  },
  methods: {
    lineTotal(item) {
      const p = Number(item.price)
      const q = Number(item.orderQuantity) || 1
      if (Number.isNaN(p)) return 0
      return p * q
    },
    async loadCountries() {
      try {
        const response = await this.$axios.get('/countries')
        this.countries = response.data
      } catch (error) {
        console.error('Error loading countries:', error)
        toast.error('Failed to load countries. Please try again.')
      }
    },

    async loadAddress() {
      try {
        const response = await this.$axios.get('/get-user-address/' + this.user.id)
        this.addresses = response.data
      } catch (error) {
        console.error('Error loading address:', error)
        toast.error('Failed to load addresses. Please try again.')
      }
    },

    fillAddressForm(address) {
      if (!address) return
      this.formData.name = address.name
      this.formData.country = address.country_id != null ? String(address.country_id) : address.country_id
      this.formData.address = address.address
      this.formData.address_2 = address.address_2
      this.formData.city = address.city
      this.formData.zip_code = address.zip_code
      this.formData.phone = address.phone || ''
    },

    onSelectSavedAddress(address) {
      this.useAlternateAddress = false
      this.fillAddressForm(address)
    },

    startAlternateAddress() {
      this.useAlternateAddress = true
      this.selectedAddressId = null
      this.saveNewAddressToProfile = true
    },

    backToSavedAddresses() {
      this.useAlternateAddress = false
      if (this.addresses.length > 0) {
        const a = this.addresses[0]
        this.selectedAddressId = a.id
        this.fillAddressForm(a)
      }
    },

    openAddressModal() {
      this.modalErrors = {}
      this.modalForm = {
        name: this.formData.name || this.user.name || '',
        phone: this.formData.phone || '',
        country_id:
          this.formData.country !== '' && this.formData.country != null
            ? Number(this.formData.country)
            : '',
        address: this.formData.address || '',
        address_2: this.formData.address_2 || '',
        city: this.formData.city || '',
        zip_code: this.formData.zip_code || '',
      }
      if (typeof this.modalForm.country_id !== 'number' && this.modalForm.country_id !== '') {
        const n = Number(this.modalForm.country_id)
        this.modalForm.country_id = Number.isNaN(n) ? '' : n
      }
      this.addressModalOpen = true
    },

    closeAddressModal() {
      this.addressModalOpen = false
    },

    validateModalForm() {
      const e = {}
      if (!this.modalForm.name || !String(this.modalForm.name).trim()) {
        e.name = 'Name is required.'
      }
      if (!this.modalForm.country_id && this.modalForm.country_id !== 0) {
        e.country_id = 'Country is required.'
      }
      if (!this.modalForm.address || !String(this.modalForm.address).trim()) {
        e.address = 'Address is required.'
      }
      if (!this.modalForm.city || !String(this.modalForm.city).trim()) {
        e.city = 'City is required.'
      }
      if (!this.modalForm.zip_code || !String(this.modalForm.zip_code).trim()) {
        e.zip_code = 'Postcode is required.'
      }
      this.modalErrors = e
      return Object.keys(e).length === 0
    },

    async submitAddressModal() {
      if (!this.validateModalForm()) return
      const token = getAuthToken()
      if (!token) {
        const msg = 'Please log in to save addresses.'
        this.modalErrors = { general: msg }
        toast.error(msg)
        return
      }
      this.modalSaving = true
      this.modalErrors = {}
      try {
        const payload = {
          name: String(this.modalForm.name).trim(),
          phone: this.modalForm.phone ? String(this.modalForm.phone).trim() : null,
          country_id: Number(this.modalForm.country_id),
          address: String(this.modalForm.address).trim(),
          address_2: this.modalForm.address_2 ? String(this.modalForm.address_2).trim() : null,
          city: String(this.modalForm.city).trim(),
          zip_code: String(this.modalForm.zip_code).trim(),
          auth_token: token,
        }
        const { data } = await this.$axios.post('/web-user-address', payload)
        const newId = data?.address?.id
        toast.success(typeof data?.message === 'string' ? data.message : 'Address saved')
        await this.loadAddress()
        this.useAlternateAddress = false
        if (newId != null) {
          this.selectedAddressId = newId
          const row = this.addresses.find((x) => String(x.id) === String(newId))
          if (row) this.fillAddressForm(row)
        }
        this.closeAddressModal()
      } catch (error) {
        const d = error.response?.data
        const msg =
          (typeof d?.message === 'string' && d.message) ||
          (d?.errors && Object.values(d.errors).flat().join(' ')) ||
          'Could not save address.'
        this.modalErrors = { general: msg }
        toast.error(msg)
      } finally {
        this.modalSaving = false
      }
    },

    async persistAddressFromForm() {
      const token = getAuthToken()
      if (!token) {
        throw new Error('Not authenticated')
      }
      const c = this.formData.country
      const payload = {
        name: this.formData.name.trim(),
        phone: this.formData.phone?.trim() || null,
        country_id: Number(c),
        address: this.formData.address.trim(),
        address_2: this.formData.address_2?.trim() || null,
        city: this.formData.city.trim(),
        zip_code: this.formData.zip_code.trim(),
        auth_token: token,
      }
      const { data } = await this.$axios.post('/web-user-address', payload)
      await this.loadAddress()
      const newId = data?.address?.id
      if (newId != null) {
        this.selectedAddressId = newId
        const row = this.addresses.find((x) => String(x.id) === String(newId))
        if (row) this.fillAddressForm(row)
      }
      this.useAlternateAddress = false
      toast.success('Address saved to your account')
    },

    validateForm() {
      this.errors = {}

      const usingSavedOnly =
        this.selectedAddressId != null &&
        this.selectedAddressId !== '' &&
        !this.useAlternateAddress

      if (usingSavedOnly) {
        return true
      }

      if (!this.formData.name) {
        this.errors.name = 'Name is required.'
      }

      if (!this.formData.country) {
        this.errors.country = 'Country is required.'
      }
      if (!this.formData.address) {
        this.errors.address = 'Address is required.'
      }
      if (!this.formData.city) {
        this.errors.city = 'City is required.'
      }
      if (!this.formData.email || !/\S+@\S+\.\S+/.test(this.formData.email)) {
        this.errors.email = 'A valid email is required.'
      }
      if (!this.formData.phone) {
        this.errors.phone = 'Phone is required.'
      }
      if (!this.formData.zip_code) {
        this.errors.zip_code = 'Postcode is required.'
      }

      return Object.keys(this.errors).length === 0
    },

    async onSubmit() {
      if (!this.validateForm()) {
        return
      }

      try {
        if (
          (!this.selectedAddressId || this.useAlternateAddress) &&
          this.saveNewAddressToProfile &&
          getAuthToken()
        ) {
          try {
            await this.persistAddressFromForm()
          } catch (persistErr) {
            const dm = persistErr?.response?.data
            const persistMsg =
              (typeof dm?.message === 'string' && dm.message) ||
              (dm?.errors && Object.values(dm.errors).flat().join(' ')) ||
              persistErr.message ||
              'Could not save address.'
            toast.error(persistMsg)
            return
          }
        }

        const payload = {
          cart: this.cartStore.cart_products.map((item) => {
            const line = {
              id: Number(item.id),
              orderQuantity: Number(item.orderQuantity),
              price: Number(item.price),
            }
            if (item.old_product_id != null && item.old_product_id !== '') {
              line.old_product_id = Number(item.old_product_id)
            }
            if (item.variant_id != null && item.variant_id !== '') {
              line.variant_id = Number(item.variant_id)
            }
            if (item.product_code) {
              line.product_code = String(item.product_code)
            }
            return line
          }),
          user_id: this.user.id != null ? Number(this.user.id) : 0,
          total: Number(this.cartStore.totalPriceQuantity.total) + Number(this.shipCost),
          order_note: this.formData.order_note,
        }

        const useSavedAddr =
          this.selectedAddressId != null &&
          this.selectedAddressId !== '' &&
          !this.useAlternateAddress

        if (useSavedAddr) {
          payload.address_id = Number(this.selectedAddressId)
        } else {
          const c = this.formData.country
          payload.new_address = {
            ...this.formData,
            country:
              c !== '' && c != null && !Number.isNaN(Number(c)) ? Number(c) : c,
          }
        }

        await this.$axios.post('/orders', payload)

        toast.success('Order placed successfully')
        this.cartStore.clear_cart({ skipConfirm: true })
        this.$router.push('/my-account')
      } catch (error) {
        console.error('Error placing order:', error)
        const d = error.response?.data
        const msg =
          (typeof d?.message === 'string' && d.message) ||
          (typeof d?.error === 'string' && d.error) ||
          (d?.errors && Object.values(d.errors).flat().join(' ')) ||
          'Failed to place the order. Please try again.'
        toast.error(msg)
      }
    },
  },
  async mounted() {
    await this.loadCountries()
    await this.loadAddress()
    this.formData.name = this.user.name
    this.formData.email = this.user.email
    this.formData.user_id = this.user.id
    if (this.addresses.length === 0 && getAuthToken()) {
      this.$nextTick(() => this.openAddressModal())
    } else if (this.addresses.length > 0) {
      const first = this.addresses[0]
      if (this.selectedAddressId == null) {
        this.selectedAddressId = first.id
      }
      const sel = this.addresses.find((a) => String(a.id) === String(this.selectedAddressId)) || first
      this.fillAddressForm(sel)
    }
  },
}
</script>

<style scoped>
.fe-checkout-page {
  padding: 2.5rem 0 4rem;
  background: linear-gradient(180deg, #f8f9fb 0%, #fff 40%);
}

.fe-checkout-page__inner {
  max-width: 1320px;
  padding-left: 1rem;
  padding-right: 1rem;
}

@media (min-width: 1400px) {
  .fe-checkout-page__inner {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }
}

.fe-checkout-empty {
  text-align: center;
  padding: 3rem 1.5rem;
  background: #fff;
  border-radius: 20px;
  border: 1px solid #eceef2;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.06);
}

.fe-checkout-empty__icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: color-mix(in srgb, var(--fe-accent, #fcbe00) 80%, #333);
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 14%, #fff);
}

.fe-checkout-empty__title {
  font-size: 1.4rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 0.5rem;
}

.fe-checkout-empty__text {
  color: #64748b;
  margin: 0 0 1.5rem;
  font-size: 0.95rem;
}

.fe-co-card {
  background: #fff;
  border-radius: 18px;
  border: 1px solid #e8ecf1;
  box-shadow: 0 10px 36px rgba(15, 23, 42, 0.07);
  padding: 1.5rem 1.5rem 1.75rem;
}

/* Wider, more readable billing block */
.fe-co-card--billing {
  padding: 1.75rem 1.5rem 2rem;
}

@media (min-width: 992px) {
  .fe-co-card--billing {
    padding: 2rem 2.25rem 2.25rem;
  }
}

@media (min-width: 1200px) {
  .fe-co-card--billing {
    padding: 2.25rem 2.5rem 2.5rem;
  }
}

.fe-co-card__head {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f5f9;
}

.fe-co-card__title {
  margin: 0 0 0.35rem;
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f172a;
}

.fe-co-card--billing .fe-co-card__title {
  font-size: clamp(1.35rem, 2.2vw, 1.55rem);
}

.fe-co-card__sub {
  margin: 0;
  font-size: 0.88rem;
  color: #64748b;
  line-height: 1.45;
}

.fe-co-card--billing .fe-co-card__sub {
  font-size: 0.95rem;
  max-width: 52rem;
}

.fe-co-label-section {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  margin: 0 0 0.75rem;
}

.fe-co-label-section--form {
  margin-top: 1.75rem;
  margin-bottom: 1rem;
}

.fe-co-muted {
  font-size: 0.88rem;
  color: #94a3b8;
  margin: 0 0 0.5rem;
}

.fe-address-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.fe-address-option {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-radius: 12px;
  border: 1px solid #e8ecf1;
  background: #fafbfc;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
}

.fe-address-option:hover {
  border-color: color-mix(in srgb, var(--fe-accent, #fcbe00) 45%, #e2e8f0);
}

.fe-address-option--active {
  border-color: var(--fe-accent, #fcbe00);
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 8%, #fff);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--fe-accent, #fcbe00) 25%, transparent);
}

.fe-address-option input {
  margin-top: 0.25rem;
  accent-color: var(--fe-accent, #fcbe00);
}

.fe-address-option__body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  text-align: left;
}

.fe-address-option__name {
  font-weight: 700;
  color: #0f172a;
  font-size: 0.92rem;
}

.fe-address-option__line {
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.35;
}

.fe-field-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569;
  margin-bottom: 0.4rem;
}

.fe-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.fe-req {
  color: #dc2626;
}

.fe-optional {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: #94a3b8;
  font-size: 0.75rem;
}

.fe-input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.8rem 1.05rem;
  min-height: 48px;
  font-size: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  color: #0f172a;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.fe-co-card--billing .fe-input,
.fe-co-card--billing .fe-select {
  font-size: 1rem;
}

.fe-co-card--billing .fe-field-label {
  font-size: 0.8rem;
  margin-bottom: 0.5rem;
}

/* Textarea: don’t use single-line min-height */
.fe-input.fe-textarea {
  min-height: 120px;
}

.fe-input::placeholder {
  color: #94a3b8;
}

.fe-input:hover {
  border-color: #cbd5e1;
}

.fe-input:focus {
  outline: none;
  border-color: var(--fe-accent, #fcbe00);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--fe-accent, #fcbe00) 22%, transparent);
}

.fe-input:disabled,
.fe-input.fe-input--locked:disabled {
  background: #f1f5f9;
  color: #475569;
  cursor: not-allowed;
  border-color: #e2e8f0;
  opacity: 1;
}

.fe-select:disabled {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
}

.fe-co-field-hint {
  margin: 0.35rem 0 0;
  font-size: 0.78rem;
  color: #94a3b8;
  line-height: 1.4;
}

.fe-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.9rem center;
  padding-right: 2.25rem;
}

.fe-textarea {
  resize: vertical;
  min-height: 120px;
  line-height: 1.55;
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
}

.fe-co-card--billing .fe-textarea {
  min-height: 130px;
}

.fe-error {
  color: #b91c1c;
  font-size: 0.8rem;
  margin-top: 0.35rem;
  font-weight: 600;
}

/* Order */
.fe-co-order {
  position: sticky;
  top: 1rem;
}

.fe-co-order__title {
  margin: 0 0 1.1rem;
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f172a;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #f1f5f9;
}

.fe-co-order__list {
  margin-bottom: 1rem;
}

.fe-co-line {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.88rem;
}

.fe-co-line:last-of-type {
  border-bottom: none;
}

.fe-co-line__info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.fe-co-line__name {
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
  word-break: break-word;
}

.fe-co-line__qty {
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 600;
}

.fe-co-line__price {
  font-weight: 700;
  color: #334155;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.fe-co-totals {
  border-top: 1px solid #eee;
  padding-top: 0.85rem;
  margin-bottom: 1.25rem;
}

.fe-co-totals__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;
  font-size: 0.88rem;
  color: #64748b;
}

.fe-co-totals__row--grand {
  margin-top: 0.35rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #e2e8f0;
  font-weight: 800;
  font-size: 1rem;
  color: #0f172a;
}

.fe-co-money {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #334155;
}

.fe-co-money--grand {
  font-size: 1.2rem;
  font-weight: 800;
  color: #0f172a;
}

.fe-co-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.85rem 1.35rem;
  border-radius: 12px;
  font-weight: 800;
  font-size: 0.92rem;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition:
    transform 0.1s ease,
    box-shadow 0.2s ease,
    filter 0.15s ease;
}

.fe-co-btn--block {
  width: 100%;
}

.fe-co-btn--primary {
  background: linear-gradient(
    135deg,
    var(--fe-accent, #fcbe00) 0%,
    color-mix(in srgb, var(--fe-accent-hover, #e5ac00) 90%, #111) 100%
  );
  color: #ffffff;
  box-shadow: 0 8px 24px color-mix(in srgb, var(--fe-accent, #fcbe00) 35%, transparent);
}

.fe-co-btn--primary:hover {
  filter: brightness(1.03);
  box-shadow: 0 10px 28px color-mix(in srgb, var(--fe-accent, #fcbe00) 42%, transparent);
  color: #ffffff;
}

.fe-co-btn--secondary {
  margin-top: 0.5rem;
  background: #fff;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
}

.fe-co-btn--secondary:hover {
  border-color: var(--fe-accent, #fcbe00);
  color: #0f172a;
}

.fe-co-btn--sm {
  padding: 0.55rem 1rem;
  font-size: 0.85rem;
}

.fe-co-btn--ghost {
  background: #fff;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.fe-co-btn--ghost:hover {
  border-color: var(--fe-accent, #fcbe00);
  color: #0f172a;
}

.fe-co-address-actions {
  margin-top: 0.75rem;
}

.fe-co-linkish {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0;
  border: none;
  background: none;
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--fe-accent, #b8860b);
  text-decoration: underline;
  cursor: pointer;
}

.fe-co-linkish:hover {
  color: #0f172a;
}

.fe-co-tip {
  margin-top: 0.75rem;
  font-size: 0.88rem;
  color: #64748b;
  line-height: 1.45;
}

.fe-co-save-profile .fe-co-check {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  user-select: none;
}

.fe-co-save-profile .fe-co-check input {
  margin-top: 0.15rem;
  accent-color: var(--fe-accent, #fcbe00);
}

.fe-co-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1080;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(3px);
}

.fe-co-modal {
  width: min(100%, 520px);
  max-height: min(90vh, 720px);
  overflow: auto;
  background: #fff;
  border-radius: 18px;
  border: 1px solid #e8ecf1;
  box-shadow: 0 24px 56px rgba(15, 23, 42, 0.2);
  padding: 1.25rem 1.35rem 1.5rem;
}

.fe-co-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.65rem;
}

.fe-co-modal__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.fe-co-modal__close {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.fe-co-modal__close:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.fe-co-modal__intro {
  margin: 0 0 1rem;
  font-size: 0.88rem;
  color: #64748b;
  line-height: 1.45;
}

.fe-co-modal__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem 1rem;
}

.fe-co-modal__field--full {
  grid-column: 1 / -1;
}

.fe-co-modal__actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f5f9;
}

@media (max-width: 520px) {
  .fe-co-modal__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 991.98px) {
  .fe-co-order {
    position: static;
  }
}
</style>